import React, { useState } from "react";
import { toast } from "sonner";
import { Calculator as CalcIcon, Plus, Minus } from "lucide-react";
import api, { inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Calculator() {
  const [pool, setPool] = useState(1000);
  const [levels, setLevels] = useState([800, 600]);
  const [result, setResult] = useState(null);

  const run = async () => {
    try {
      const { data } = await api.post("/referral/calculator", { pool: Number(pool), selected: levels.map(Number) });
      setResult(data);
    } catch { toast.error("Could not calculate. Check your inputs."); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-14 min-h-[70vh]">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center"><CalcIcon className="w-6 h-6" /></div>
        <h1 className="font-display text-3xl font-extrabold text-[#0B223D]">Referral Reward Calculator</h1>
      </div>
      <p className="text-slate-500 mb-8">See exactly how a campaign pool splits across your referral chain. The company campaign rate is configured separately — this only shows the referral distribution.</p>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
        <div>
          <label className="text-sm font-semibold text-slate-600">Campaign payout pool (₹)</label>
          <Input type="number" value={pool} onChange={(e) => setPool(e.target.value)} className="mt-1.5 h-11 font-mono-num" data-testid="calc-pool" />
        </div>
        <div>
          <label className="text-sm font-semibold text-slate-600">Each level's selected payout (top → bottom). Last level converts.</label>
          <div className="space-y-2 mt-2">
            {levels.map((v, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-sm text-slate-400 w-16">Level {i + 1}</span>
                <Input type="number" value={v} data-testid={`calc-level-${i}`}
                  onChange={(e) => setLevels(levels.map((x, j) => (j === i ? e.target.value : x)))} className="h-11 font-mono-num" />
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={() => setLevels([...levels, Math.max(0, (Number(levels[levels.length - 1]) || 100) - 100)])} data-testid="calc-add-level"><Plus className="w-4 h-4 mr-1" /> Add level</Button>
            {levels.length > 1 && <Button variant="outline" size="sm" onClick={() => setLevels(levels.slice(0, -1))}><Minus className="w-4 h-4 mr-1" /> Remove</Button>}
          </div>
        </div>
        <Button onClick={run} data-testid="calc-run-btn" className="w-full bg-[#0B223D] hover:bg-[#06182B] text-white font-bold h-12 rounded-xl">Calculate rewards</Button>
      </div>

      {result && (
        <div className="bg-[#0B223D] text-white rounded-2xl p-6 mt-6" data-testid="calc-result">
          <h3 className="font-display font-bold text-lg mb-4">Distribution</h3>
          {result.distribution.map((d, i) => (
            <div key={i} className="flex justify-between py-3 border-b border-white/10 last:border-0">
              <span className="text-white/80">{d.label}</span>
              <span className="font-mono-num font-bold text-teal-300">+{inr(d.amount)}</span>
            </div>
          ))}
          <div className="flex justify-between mt-4 pt-3 border-t border-white/20"><span className="text-white/60">Total distributed</span><span className="font-mono-num font-bold">{inr(result.total)}</span></div>
        </div>
      )}
    </div>
  );
}
